import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';

const MentionsInput = React.forwardRef(({
  inputText,
  setInputText,
  placeholder = "What can I take off your mind right now?",
  onPasteFile,
  isDarkMode = false,
  disabled = false,
  onKeyDown,
  style = {},
  className = ""
}, ref) => {
  // Custom resize handle logic
  const textareaRef = useRef(null);
  const mentionChipRef = useRef(null);
  const backspaceOnEmptyCount = useRef(0);
  const [height, setHeight] = useState(80);
  const isDragging = useRef(false);
  const [dragStartY, setDragStartY] = useState(null);
  const [dragStartHeight, setDragStartHeight] = useState(null);
  const [isManualResize, setIsManualResize] = useState(false);

  // Mention chip state
  const [selectedMention, setSelectedMention] = useState(null); // { label: "@Notion", action: "notion" }

  // Expose helpers to parent via ref
  useImperativeHandle(ref, () => ({
    getEffectiveQuery: () =>
      selectedMention
        ? `${selectedMention.label}: ${inputText}`
        : inputText,
    clearMention: () => setSelectedMention(null),
  }));

  // Update textarea left-padding via DOM when chip appears/disappears
  useEffect(() => {
    if (!textareaRef.current) return;
    if (selectedMention && mentionChipRef.current) {
      const chipW = mentionChipRef.current.offsetWidth;
      textareaRef.current.style.setProperty("padding-left", `${chipW + 14}px`, "important");
    } else {
      textareaRef.current.style.setProperty("padding-left", "12px", "important");
    }
  }, [selectedMention]);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    setDragStartY(e.clientY);
    setDragStartHeight(height);
    setIsManualResize(true);
    document.body.style.cursor = "ns-resize";
    e.preventDefault();
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    setDragStartY(null);
    setDragStartHeight(null);
    document.body.style.cursor = "default";
  };

  const handleMouseMove = (e) => {
    if (isDragging.current && textareaRef.current && dragStartY !== null) {
      const deltaY = dragStartY - e.clientY;
      let newHeight = dragStartHeight + deltaY;
      newHeight = Math.max(80, Math.min(newHeight, 200));
      setHeight(newHeight);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp, dragStartY, dragStartHeight]);

  const [showMentions, setShowMentions] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const actionLists = {
    "@Notion": "notion",
    "@Task": "task",
    "@Lists": "lists",
    "@Notes": "notes",
    "@Contacts": "contacts",
    "@Groceries": "groceries",
    "@GoogleCalendar": "google_calendar",
    "@Gmail": "gmail",
    "@GoogleDrive": "google_drive",
    "@GoogleDocs": "google_docs",
    "@GoogleSheets": "google_sheets",
    "@DeepResearch": "deep_research",
    "@WebSearch": "web_search",
    "@YoutubeScraping": "youtube_scraping",
    "@InstagramScraping": "instagram_scraping",
    "@ScrapeUrl": "scrape_url",
    "@Reminders": "reminders",
    "@SecondBrain": "second_brain",
    "@TikTok": "tik_tok",
    "@Topics": "sessions",
    "@Meetings": "meetings",
    "@SMS": "sms",
    "@Files": "files",
    "@Notifications": "notifications",
    "@ImageGeneration": "image_generation",
    "@ScheduledTasks": "scheduled_tasks",
    "@Ideas": "ideas",
  };

  // Select mention → set chip, keep only clean text in textarea
  const selectMention = (mention) => {
    let cleanText = inputText.replace(/@[^:]*:/g, '').trim();

    const textarea = textareaRef?.current;
    const value = inputText;
    const selectionStart = textarea ? textarea.selectionStart : value.length;
    const atIndex = value.lastIndexOf("@", selectionStart - 1);

    if (atIndex !== -1) {
      let endIndex = atIndex + 1;
      while (endIndex < value.length && /[\w]/.test(value[endIndex])) {
        endIndex++;
      }
      const before = value.slice(0, atIndex);
      const after = value.slice(endIndex);
      cleanText = (before + after).replace(/@[^:]*:/g, '').trim();
    }

    setSelectedMention({ label: mention, action: actionLists[mention] });
    setInputText(cleanText);

    setTimeout(() => {
      if (textareaRef?.current) {
        textareaRef.current.setSelectionRange(cleanText.length, cleanText.length);
        textareaRef.current.focus();
      }
    }, 0);

    setShowMentions(false);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setInputText(value);

    if (value.includes("@")) {
      const caretPosition = e.target.selectionStart;
      const textBeforeCaret = value.slice(0, caretPosition);
      const lastAtIndex = textBeforeCaret.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        const query = textBeforeCaret.slice(lastAtIndex + 1);
        const filteredActions = Object.keys(actionLists).filter((action) =>
          action.toLowerCase().includes(query.toLowerCase())
        );

        if (filteredActions.length > 0) {
          setMentions(filteredActions);
          setShowMentions(true);
          setHighlightedIndex(0);
        } else {
          setShowMentions(false);
        }
      }
    } else {
      setShowMentions(false);
    }

    if (!isManualResize && textareaRef.current) {
      const scrollHeight = textareaRef.current.scrollHeight;
      if (scrollHeight > height) {
        setHeight(Math.min(scrollHeight, 800));
      }
    }
  };

  useEffect(() => {
    if (inputText.length === 0) {
      setIsManualResize(false);
      setHeight(80);
    }
  }, [inputText]);

  useEffect(() => {
    if (showMentions) {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setShowMentions(false);
          setHighlightedIndex(-1);
          if (textareaRef.current) textareaRef.current.focus();
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev < mentions.length - 1 ? prev + 1 : 0;
            if (dropdownRef.current) {
              const itemsContainer = dropdownRef.current.children[1];
              if (itemsContainer && itemsContainer.children[nextIndex]) {
                itemsContainer.children[nextIndex].scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
            }
            return nextIndex;
          });
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : mentions.length - 1;
            if (dropdownRef.current) {
              const itemsContainer = dropdownRef.current.children[1];
              if (itemsContainer && itemsContainer.children[nextIndex]) {
                itemsContainer.children[nextIndex].scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
            }
            return nextIndex;
          });
        }
        if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
          e.preventDefault();
          selectMention(mentions[highlightedIndex]);
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showMentions, highlightedIndex, mentions, inputText, textareaRef]);

  const getMentionIcon = (mention) => {
    if (mention.includes("Task")) return "bi-check2-square";
    if (mention.includes("Notes")) return "bi-journal-text";
    if (mention.includes("Lists")) return "bi-list-ul";
    if (mention.includes("Gmail")) return "bi-envelope";
    if (mention.includes("Calendar")) return "bi-calendar3";
    if (mention.includes("Contacts")) return "bi-person-lines-fill";
    if (mention.includes("Groceries")) return "bi-cart3";
    if (mention.includes("Notion")) return "bi-box";
    if (mention.includes("DeepResearch")) return "bi-search";
    if (mention.includes("WebSearch")) return "bi-globe";
    if (mention.includes("Youtube")) return "bi-youtube";
    if (mention.includes("Instagram")) return "bi-instagram";
    if (mention.includes("TikTok")) return "bi-tiktok";
    if (mention.includes("ScrapeUrl")) return "bi-link-45deg";
    if (mention.includes("Reminders")) return "bi-bell";
    if (mention.includes("SecondBrain")) return "bi-lightbulb";
    if (mention.includes("Memory")) return "bi-cpu";
    if (mention.includes("Topics")) return "bi-chat-dots";
    if (mention.includes("Meetings")) return "bi-camera-video";
    if (mention.includes("SMS")) return "bi-chat-left-text";
    if (mention.includes("KnowledgeBase")) return "bi-book";
    if (mention.includes("GoogleDrive")) return "bi-cloud";
    if (mention.includes("GoogleDocs")) return "bi-file-earmark-text";
    if (mention.includes("GoogleSheets")) return "bi-table";
    if (mention.includes("Files")) return "bi-folder";
    if (mention.includes("Notifications")) return "bi-bell-fill";
    if (mention.includes("ImageGeneration")) return "bi-image";
    if (mention.includes("ScheduledTasks")) return "bi-clock-history";
    if (mention.includes("Queue")) return "bi-list-task";
    if (mention.includes("Ideas")) return "bi-lightbulb";
    return "bi-at";
  };

  const handleKeyDown = (e) => {
    if (showMentions && (e.key === "Enter" && !e.shiftKey || e.key === "Tab" || e.key === "Escape")) {
      return;
    }
    if (e.key === "Backspace" && selectedMention && e.target.value === "") {
      backspaceOnEmptyCount.current += 1;
      if (backspaceOnEmptyCount.current >= 2) {
        backspaceOnEmptyCount.current = 0;
        setSelectedMention(null);
      }
      return;
    } else if (e.key !== "Backspace") {
      backspaceOnEmptyCount.current = 0;
    }
    if (onKeyDown) onKeyDown(e);
  };

  const handlePaste = (e) => {
    const clipboardItems = e.clipboardData.items;
    let hasFile = false;

    for (let item of clipboardItems) {
      if (item.kind === "file") {
        e.preventDefault();
        hasFile = true;
        const file = item.getAsFile();
        const validTypes = [
          "image/jpeg", "image/png", "image/gif", "image/bmp", "image/tiff", "image/webp",
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/msword",
          "text/plain",
        ];

        if (validTypes.includes(file.type)) {
          const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
          const fileName = `${file.name.split(".").slice(0, -1).join(".")}_${timestamp}.${file.name.split(".").pop()}`;

          setSelectedFile(file);

          const reader = new FileReader();
          reader.onloadend = () => {
            const previewObj = file.type.startsWith("image/")
              ? { file, type: "image", url: reader.result, name: fileName }
              : { file, type: "document", url: reader.result, name: fileName };
            setPreviewFile(previewObj);
            if (typeof onPasteFile === "function") onPasteFile(file, previewObj);
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }

    if (!hasFile) return;
  };

  return (
    <div style={{ position: 'relative', width: '100%', ...style }} className={className}>
      {/* Mentions Dropdown */}
      {showMentions && (
        <div
          ref={dropdownRef}
          tabIndex={0}
          style={{
            position: "absolute",
            bottom: "100%",
            left: "0",
            marginBottom: "8px",
            background: "var(--background)",
            color: "var(--text)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            boxShadow: isDarkMode ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.15)",
            zIndex: 1000,
            width: "220px",
            overflow: "hidden",
          }}
          onBlur={() => setTimeout(() => setShowMentions(false), 100)}
        >
          <div style={{
            padding: "10px 14px",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "var(--background)",
            borderRadius: "12px 12px 0 0",
          }}>
            <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--text)", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Actions
            </span>
            <span style={{ fontSize: "10px", color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)", display: "flex", gap: "4px", alignItems: "center" }}>
              <kbd style={{ padding: "2px 5px", borderRadius: "4px", background: isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)", color: isDarkMode ? "#fff" : "#333", fontSize: "9px", fontWeight: "500" }}>Tab</kbd>
              <span>to select</span>
            </span>
          </div>
          <div className="mentions-scroll-container" style={{ maxHeight: "250px", overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: isDarkMode ? "#555 transparent" : "#ccc transparent" }}>
            {mentions.map((mention, index) => (
              <div
                key={index}
                style={{
                  padding: "10px 14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  backgroundColor: highlightedIndex === index ? "var(--teal-bg)" : "transparent",
                  color: highlightedIndex === index ? "#fff" : "var(--text)",
                  transition: "all 0.15s ease",
                  borderLeft: highlightedIndex === index ? "3px solid var(--orange-bg)" : "3px solid transparent",
                }}
                onClick={() => selectMention(mention)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <i className={`bi ${getMentionIcon(mention)}`} style={{ fontSize: "14px", opacity: highlightedIndex === index ? 1 : 0.7 }}></i>
                <span style={{ fontSize: "13px", fontWeight: highlightedIndex === index ? "500" : "400" }}>
                  {mention.replace("@", "")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom resize handle */}
      <div
        style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", cursor: "ns-resize", zIndex: 2, width: 40, height: 12, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent" }}
        onMouseDown={handleMouseDown}
      >
        <span style={{ fontSize: 18, color: "#bbb", letterSpacing: 2 }}>•••••</span>
      </div>

      {/* @mention chip — absolutely positioned inside the input border */}
      {selectedMention && (
        <span
          ref={mentionChipRef}
          style={{
            position: "absolute",
            left: "8px",
            top: "22px", // below resize handle (paddingTop: 20 on textarea)
            zIndex: 2,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "2px 6px 2px 10px",
            borderRadius: "20px",
            background: "rgba(13, 148, 136, 0.15)",
            border: "1px solid var(--teal-bg, #0d9488)",
            color: "var(--teal-bg, #0d9488)",
            fontSize: "12px",
            fontWeight: "600",
            whiteSpace: "nowrap",
            pointerEvents: "auto",
          }}
        >
          {selectedMention.label}
          <button
            onMouseDown={(e) => { e.preventDefault(); setSelectedMention(null); }}
            style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: "0 2px", fontSize: "14px", lineHeight: 1, display: "flex", alignItems: "center" }}
            title="Remove"
          >×</button>
        </span>
      )}

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={inputText}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        onPaste={handlePaste}
        className="form-control search-input-box"
        style={{
          width: '100%',
          resize: 'none',
          flex: 1,
          minHeight: 80,
          maxHeight: 200,
          height: height,
          paddingTop: 20,
        }}
      />
    </div>
  );
});

MentionsInput.displayName = 'MentionsInput';

export default MentionsInput;
